import React, { useState, useEffect, useMemo } from 'react';
import { GeoJSON } from 'react-leaflet';
import { useMap } from 'react-leaflet';

const PipelineOverlay = ({ pipelineData, isVisible = true }) => {
  const map = useMap();
  const [viewportBounds, setViewportBounds] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(15);
  const [dynamicPipelineData, setDynamicPipelineData] = useState(null);

  // Update viewport bounds and zoom level when map moves
  useEffect(() => {
    const updateBounds = () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      setViewportBounds(bounds);
      setZoomLevel(zoom);
    };

    // Initial bounds
    updateBounds();

    // Listen for map events
    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);

    return () => {
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  // Load pipeline data based on zoom level
  useEffect(() => {
    const loadPipelineData = async () => {
      let fileToLoad;
      
      if (zoomLevel >= 15) {
        fileToLoad = '/data/Trasy_eustream_wgs_ultra_smooth.geojson';
      } else if (zoomLevel >= 10) {
        fileToLoad = '/data/Trasy_eustream_wgs_optimized.geojson';
      } else {
        fileToLoad = '/data/Trasy_eustream_wgs_lightweight.geojson';
      }
      
      try {
        console.log(`Loading pipeline data: ${fileToLoad} for zoom level ${zoomLevel}`);
        const response = await fetch(fileToLoad);
        if (response.ok) {
          const data = await response.json();
          console.log(`Successfully loaded ${fileToLoad} with ${data.features?.length || 0} features`);
          setDynamicPipelineData(data);
        } else {
          console.warn(`Failed to load ${fileToLoad}: ${response.status}`);
        }
      } catch (error) {
        console.warn('Could not load pipeline data:', error);
      }
    };

    if (isVisible) {
      loadPipelineData();
    }
  }, [isVisible, zoomLevel]);


  // Filter pipeline features based on viewport and zoom level
  const visiblePipelines = useMemo(() => {
    const dataToUse = dynamicPipelineData || pipelineData;
    if (!dataToUse || !dataToUse.features) {
      console.log('No pipeline data available');
      return dataToUse;
    }
    
    // If no viewport bounds yet, show all features (fallback)
    if (!viewportBounds) {
      console.log('No viewport bounds, showing all features');
      return dataToUse;
    }

    // Determine max features to show based on zoom level
    let maxFeatures;
    if (zoomLevel >= 15) maxFeatures = 1000;      // High detail - show many pipelines
    else if (zoomLevel >= 12) maxFeatures = 500;  // Medium detail
    else if (zoomLevel >= 10) maxFeatures = 200;  // Low detail
    else maxFeatures = 100;                       // Overview - show more routes

    // Expand viewport bounds slightly to ensure continuity
    const expandedBounds = {
      north: viewportBounds.getNorth() + 0.01,
      south: viewportBounds.getSouth() - 0.01,
      east: viewportBounds.getEast() + 0.01,
      west: viewportBounds.getWest() - 0.01
    };

    const filteredFeatures = dataToUse.features.filter(feature => {
      if (!feature.geometry || feature.geometry.type !== 'LineString') {
        return false;
      }

      // Check if any coordinate of the LineString intersects with expanded viewport bounds
      return feature.geometry.coordinates.some(coord => {
        const [lng, lat] = coord;
        return lat >= expandedBounds.south && 
               lat <= expandedBounds.north && 
               lng >= expandedBounds.west && 
               lng <= expandedBounds.east;
      });
    });

    // Sort by pipeline diameter (larger pipelines first) and limit
    const sortedFeatures = filteredFeatures.sort((a, b) => {
      const diameterA = parseInt(a.properties?.DIMENZIA || '0');
      const diameterB = parseInt(b.properties?.DIMENZIA || '0');
      return diameterB - diameterA;
    });

    const limitedFeatures = sortedFeatures.slice(0, maxFeatures);

    console.log(`Pipeline filtering: ${filteredFeatures.length} in viewport, showing ${limitedFeatures.length} (max: ${maxFeatures})`);

    return {
      ...dataToUse,
      features: limitedFeatures
    };
  }, [dynamicPipelineData, pipelineData, viewportBounds, zoomLevel]);

  // Style function for pipeline lines
  const stylePipeline = (feature) => {
    const diameter = feature.properties?.DIMENZIA;
    
    // Base style
    const baseStyle = {
      color: '#2c5aa0', // Blue color for pipelines
      weight: 3,
      opacity: 0.8,
      fillOpacity: 0
    };

    // Adjust weight based on pipeline diameter
    if (diameter) {
      const diameterNum = parseInt(diameter);
      if (diameterNum >= 1000) {
        baseStyle.weight = 5; // Thicker for large pipelines
      } else if (diameterNum >= 600) {
        baseStyle.weight = 4;
      } else {
        baseStyle.weight = 3;
      }
    }

    return baseStyle;
  };

  // Popup content for pipeline features
  const onEachFeature = (feature, layer) => {
    if (feature.properties) {
      const props = feature.properties;
      const popupContent = `
        <div class="pipeline-popup">
          <h4 class="font-semibold text-blue-800 mb-2">Pipeline Information</h4>
          <div class="space-y-1 text-sm">
            ${props.TRASA ? `<div><strong>Route:</strong> ${props.TRASA}</div>` : ''}
            ${props.LINIA ? `<div><strong>Line:</strong> ${props.LINIA}</div>` : ''}
            ${props.DIMENZIA ? `<div><strong>Diameter:</strong> ${props.DIMENZIA}mm</div>` : ''}
            ${props.DLZKA ? `<div><strong>Length:</strong> ${props.DLZKA.toFixed(2)}m</div>` : ''}
            ${props.POZNAMKA ? `<div><strong>Notes:</strong> ${props.POZNAMKA}</div>` : ''}
          </div>
        </div>
      `;
      layer.bindPopup(popupContent);
    }
  };

  if (!isVisible) {
    return null;
  }

  // Show loading state or fallback if no data available
  if (!visiblePipelines || !visiblePipelines.features) {
    return null;
  }

  return (
    <GeoJSON
      data={visiblePipelines}
      style={stylePipeline}
      onEachFeature={onEachFeature}
      zIndex={1} // Below measurements (zIndex 2) but above base map
    />
  );
};

export default PipelineOverlay;
